import { camelCase } from 'lodash';
import React from 'react';
import { getNodeTypeFromClassName } from '@bay-email/core';

const domParser = new DOMParser();
const VALID_ATTRIBUTE_NAME = /^[a-zA-Z_:][a-zA-Z0-9:._-]*$/;

export function getChildSelector(selector: string, index: number) {
	return `${selector}-${index}`;
}

export function HtmlStringToPreviewReactNodes(content: string) {
	let doc = domParser.parseFromString(content, 'text/html'); // The average time is about 1.4 ms
	hoistHeadStylesIntoBody(doc);
	const reactNode = (
		<>
			{[...doc.body.childNodes].map((node, index) => (
				<RenderReactNode
					selector={getChildSelector('0', index)}
					node={node as any}
					index={index}
					key={index}
				/>
			))}
		</>
	);

	return reactNode;
}

function hoistHeadStylesIntoBody(doc: Document) {
	const headNodes = [...doc.head.querySelectorAll('style,link[rel="stylesheet"]')];
	if (headNodes.length === 0) return;

	const fragment = doc.createDocumentFragment();

	headNodes.forEach(node => {
		if (node.tagName.toLowerCase() === 'style') {
			fragment.appendChild(node.cloneNode(true));
			return;
		}

		const href = node.getAttribute('href');
		if (!href) return;

		const styleNode = doc.createElement('style');
		styleNode.textContent = `@import url("${href}");`;
		fragment.appendChild(styleNode);
	});

	doc.body.prepend(fragment);
}

const RenderReactNode = React.memo(function ({
	node,
	index,
	selector,
}: {
	node: HTMLElement;
	index: number;
	selector: string;
}): React.ReactElement {
	const attributes: { [key: string]: string } = {
		'data-selector': selector,
	};
	node.getAttributeNames?.().forEach(att => {
		if (att && VALID_ATTRIBUTE_NAME.test(att)) {
			attributes[att] = node.getAttribute(att) || '';
		}
	});

	if (node.nodeType === Node.COMMENT_NODE) return <></>;

	if (node.nodeType === Node.TEXT_NODE) {
		return <>{node.textContent}</>;
	}

	if (node.nodeType === Node.ELEMENT_NODE) {
		const tagName = node.tagName.toLowerCase();
		if (tagName === 'meta') return <></>;

		if (tagName === 'style') {
			return React.createElement(tagName, {
				key: index,
				...attributes,
				dangerouslySetInnerHTML: { __html: node.textContent },
			});
		}

		const blockType = getNodeTypeFromClassName(node.classList);

		if (attributes['data-contenteditable'] === 'true') {
			return React.createElement(tagName, {
				key: performance.now(),
				...attributes,
				style: getStyle(node.getAttribute('style')),
				dangerouslySetInnerHTML: { __html: node.innerHTML },
			});
		}

		const reactNode = React.createElement(tagName, {
			key: index,
			...attributes,
			style: getStyle(node.getAttribute('style')),
			children:
				node.childNodes.length === 0
					? null
					: [...node.childNodes].map((n, i) => (
							<RenderReactNode
								selector={getChildSelector(selector, i)}
								key={i}
								node={n as any}
								index={i}
							/>
						)),
		});

		return <>{reactNode}</>;
	}

	return <></>;
});

function getStyle(styleText: string | null) {
	if (!styleText) return undefined;
	return styleText.split(';').reduceRight((a: any, b: any) => {
		const arr = b.split(/\:(?!\/)/);
		if (arr.length < 2) return a;
		// Remove !important since React inline styles don't support it
		const value = arr[1].trim().replace(/\s*!important\s*$/, '');
		a[camelCase(arr[0].trim())] = value;
		return a;
	}, {});
}
